'use client';

/**
 * The daily treasure chest.
 *
 * A once-a-day reward that costs nothing and cannot be farmed. It is tied to
 * the streak rather than to a spin, so the "gamble" framing common in kids'
 * apps is avoided entirely: there is no randomness, the reward is stated
 * before opening, and showing up more often is the only way to increase it.
 */
import React, { useState } from 'react';
import { useKids } from '../context/KidsProvider';
import { chestReward } from '../data/shop';
import { kidsAudio } from '../lib/audio';

export default function DailyChest() {
  const { profile, canClaimChest, claimChest } = useKids();
  const [opened, setOpened] = useState<{ coins: number; label: string; emoji: string } | null>(
    null
  );
  const [shaking, setShaking] = useState(false);

  if (!profile) return null;

  const preview = chestReward(profile.streak.current);
  const tomorrow = chestReward(profile.streak.current + 1);

  const open = () => {
    setShaking(true);
    kidsAudio.play('chest');
    window.setTimeout(() => {
      const reward = claimChest();
      if (reward) {
        setOpened(reward);
        kidsAudio.play('coin');
      }
      setShaking(false);
    }, 520);
  };

  return (
    <section
      className="k-card p-5 text-center relative overflow-hidden"
      style={{ background: 'var(--k-mango-soft)', borderColor: 'var(--k-mango)' }}
    >
      <h2 className="k-eyebrow">Daily chest</h2>

      <div
        className={shaking ? 'k-anim-wiggle' : opened || !canClaimChest ? '' : 'k-anim-bob'}
        style={{ fontSize: 54, lineHeight: 1.2 }}
        aria-hidden="true"
      >
        {opened ? '🪙' : canClaimChest ? preview.emoji : '✅'}
      </div>

      {opened ? (
        <div className="k-anim-pop">
          <p className="font-extrabold text-lg">+{opened.coins} coins!</p>
          <p className="text-sm text-muted-foreground">
            {opened.label} opened. Come back tomorrow.
          </p>
        </div>
      ) : canClaimChest ? (
        <>
          <p className="font-extrabold">{preview.label}</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {preview.coins} coins waiting for you.
          </p>
          <button type="button" onClick={open} className="k-btn k-btn-primary k-btn-sm mt-3">
            Open it
          </button>
        </>
      ) : (
        <>
          <p className="font-extrabold">Opened today</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            Practise tomorrow for a{tomorrow.label.startsWith('a') ? 'n' : ''}{' '}
            {tomorrow.label.toLowerCase()} worth {tomorrow.coins} coins.
          </p>
        </>
      )}
    </section>
  );
}
